import React, { FC, useContext, useEffect, useState } from 'react'

// Components
import MainModal from './common/MainModal'
import { ScratchModalHeader } from './components/ScratchModalHeader'
import { ScratchWarning } from './components/ScratchWarning'

// Redux
import { useAppSelector } from '../../store/hooks'
import { selectRecipientR } from '../../store/exhibitor/exhibitorSlice'

// Models
import { IUserInterface } from '../../models/users/user.interface'
import { IRegistrationTicketInterface } from '../../models/registration-tickets/registrationTicket.interface'
import { IRegistrationFeesInterface } from '../../models/registration-fees/registrationFees.interface'
import { IRegistrationByDayInterface } from '../../models/registrations-by-day/registrationByDay.interface'
import { IScratchPayload } from '../../types/payment'

// Constants
import { MESSAGES_CONST } from '../../const/messages-const'
import { MODAL_CONSTS } from '../../const/modal-const'

// Services
import { httpService } from '../../services/httpService'

// Helpers
import { toastFunctionsObj } from '../../helpers/ToasterHelper'
import { getFloatPrice } from '../../helpers/price'
import { getCurrency } from '../../helpers/payment'
import { getScratchItems } from '../../helpers/scratch'
import EventReviewPublishService from '../../services/firebase-service/event-review-publish/EventReviewPublishService'
import { IEventReviewPublish } from '../../models/event-review-publish/event-review-publish.interface'
import { HandleModalContext } from '../../layout/mainlayout/MainLayout'

interface PropsConfirmScratchFullEntryModal {
  dataToPassOn: {
    totalCost: number
    registrationDocId: string
    exhibitor: IUserInterface
    registrations: IRegistrationByDayInterface[]
    fees: IRegistrationFeesInterface[]
    tickets: IRegistrationTicketInterface[]
    isChargeSalesTax: boolean
    eventId: string
    onSave?: () => Promise<void>
  }
}

export const ConfirmScratchFullEntryModal: FC<PropsConfirmScratchFullEntryModal> = ({
  dataToPassOn,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const recipientR = useAppSelector(selectRecipientR)
  const { registrations, exhibitor, totalCost, fees, tickets, eventId } = dataToPassOn
  const [loading, setLoading] = useState(false)
  const [eventReviewPublish, setEventReviewPublish] = useState<IEventReviewPublish | null>(null)

  const scratchHandler = async () => {
    setLoading(true)

    try {
      const { scratchedRegistrations, scratchedFees, scratchedTickets } = getScratchItems({
        registrationsByDay: registrations.filter((item) => !item.isScratched),
        fees: fees.filter((item) => !item.isScratched),
        tickets: tickets.filter((item) => !item.isScratched),
        chargeSalesTax: eventReviewPublish?.EventPaymentSettings?.chargeSalesTax ?? false,
      })

      const payloadData: IScratchPayload = {
        registrationDocId: dataToPassOn.registrationDocId,
        scratchedRegistrationByDayItems: scratchedRegistrations.scratchedItems,
        scratchedFeesItems: scratchedFees.scratchedItems,
        scratchedTicketItems: scratchedTickets.scratchedItems,
        isChargeSalesTax: dataToPassOn.isChargeSalesTax,
        isAlreadyScratched: false,
      }

      await httpService({
        url: 'scratch_items',
        method: 'POST',
        data: payloadData,
      })

      let totalScratchAmount =
        scratchedRegistrations.scratchTotal +
        scratchedRegistrations.scratchTotalSalesTax +
        scratchedFees.scratchTotal +
        scratchedFees.scratchTotalSalesTax +
        scratchedTickets.scratchTotal +
        scratchedTickets.scratchTotalSalesTax

      handleModalContext?.handleModal?.(false, MODAL_CONSTS.CONFIRM_SCRATCH_FULL_ENTRY)
      handleModalContext?.handleModal?.(true, MODAL_CONSTS.EXHIBITOR_SCRATCH_CONFIRM_MODAL, {
        ...handleModalContext?.dataToPassOn,
        ...recipientR.data,
        user: dataToPassOn.exhibitor,
        scratchTotal: totalScratchAmount / 100,
        onSave: dataToPassOn.onSave,
      })
    } catch (error: any) {
      console.error(error, 'error')
      toastFunctionsObj.error({
        message: error?.response?.data?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const getData = async () => {
      if (!eventId) return

      try {
        const eventReviewPublishInDb = await EventReviewPublishService.getById(eventId)
        setEventReviewPublish(eventReviewPublishInDb)
      } catch (error) {
        console.error('error: ', error)
      }
    }
    getData().then()
  }, [eventId])

  const currency = getCurrency(eventReviewPublish?.EventPaymentSettings?.currency)

  return (
    <MainModal
      customTitle={
        <div className="text-SeabiscuitDark200ThemeColor text-[25px] font-bold">
          Confirm Scratch
        </div>
      }
      size="lg"
      show={true}
      onHide={() =>
        handleModalContext?.handleModal?.(false, MODAL_CONSTS.CONFIRM_SCRATCH_FULL_ENTRY)
      }
      type="CONFIRM_SCRATCH_FULL_ENTRY"
      buttons={[
        {
          label: `Scratch ${getFloatPrice(`${currency}${totalCost}`)}`,
          loading: loading,
          disabled: loading,
          className: '!w-full h-[58px]',
          bgClass: '!bg-SeabiscuitMainThemeColor',
          onClick: scratchHandler,
          textClass: '!text-white uppercase',
          onHoverClass: 'hover:!bg-SeabiscuitMainThemeColorDark',
        },
        {
          label: 'CANCEL',
          loading: loading,
          disabled: loading,
          className: '!w-full',
          bgClass: '!bg-SeabiscuitIconThemeColor/10 border-0 !mt-2 h-[58px]',
          onClick: () =>
            handleModalContext?.handleModal?.(false, MODAL_CONSTS.CONFIRM_SCRATCH_FULL_ENTRY),
          textClass: '!text-[#8597b3] uppercase',
          onHoverClass: 'hover:!bg-SeabiscuitIconThemeColor/20',
        },
      ]}
      className="z-[50]"
      overlayClassName="z-[49]"
    >
      {exhibitor && (
        <div className={'flex flex-col gap-[22px] pt-5 w-full text-SeabiscuitDark200ThemeColor'}>
          <ScratchModalHeader
            totalCost={totalCost}
            exhibitor={exhibitor}
            totalEntries={registrations.length}
            currency={currency}
          />
          <ScratchWarning />
          <ScratchWarning isInfo />
        </div>
      )}
    </MainModal>
  )
}
